<script setup lang="ts">
import { computed } from 'vue'
import { useHead } from '@unhead/vue'
import { RouterView, useRoute, useRouter } from 'vue-router'

import WorkbenchSidebar from './WorkbenchSidebar.vue'
import WorkbenchTopbar from './WorkbenchTopbar.vue'
import { getWorkbenchNavLabel, getWorkbenchToolLabel } from './workbenchNav'
import { workbenchPath } from './workbenchPaths'
import { brand } from '../config/brand'
import '../styles/workbench.css'

const route = useRoute()
const router = useRouter()

const sectionLabel = computed(() => {
  const n = String(route.name || '')
  if (n === 'wb-subaccounts') return '账号管理'
  if (n === 'wb-affiliate') return '账号管理'
  return getWorkbenchNavLabel(route.name) || ''
})
const toolLabel = computed(() => {
  const n = String(route.name || '')
  if (n === 'wb-subaccounts') return '子账号管理'
  if (n === 'wb-affiliate') return '分销管理'
  return getWorkbenchToolLabel((route.query as any)?.tool) || ''
})
const breadcrumb = computed(() => [sectionLabel.value, toolLabel.value].filter(Boolean))
const noPageScroll = computed(() => route.name === 'wb-batch' || route.name === 'wb-tasks' || route.name === 'wb-recharge')
const pagePaddingClass = computed(() => (route.name === 'wb-tasks' ? 'px-8 pt-6 pb-0' : 'px-8 py-8'))

function goRecharge() {
  router.push(workbenchPath('/recharge'))
}

const siteUrl = ((import.meta.env.VITE_SITE_URL as string | undefined) || 'https://www.wuhenai.com').replace(/\/+$/, '')
const baseUrl = (import.meta.env.BASE_URL as string | undefined) || '/'

function normalizeCanonicalPath(p: string) {
  const s = String(p || '').trim() || '/'
  const withSlash = s.startsWith('/') ? s : `/${s}`
  return withSlash.endsWith('/') ? withSlash : `${withSlash}/`
}

const htmlLang = computed(() => 'zh-CN')
const canonicalPath = computed(() => {
  const p = String(route.path || '').trim() || '/'
  const full = p.startsWith('/ai-workbench') ? p : `/ai-workbench${p.startsWith('/') ? p : `/${p}`}`
  return normalizeCanonicalPath(full)
})
const canonical = computed(() => `${siteUrl}${canonicalPath.value}`)
const pageUrl = computed(() => `${siteUrl}${String(route.fullPath || '').split('#')[0] || canonicalPath.value}`)
const socialImage = computed(() => {
  const b = baseUrl === '/' ? '/' : baseUrl.replace(/\/?$/, '/')
  return `${siteUrl}${b}${brand.ogImagePath.replace(/^\//, '')}`
})

const pageTitle = computed(() => {
  const tool = String(toolLabel.value || '').trim()
  const name = String(route.name || '').trim()
  const section = String(sectionLabel.value || '').trim()
  if (tool) return `${tool} - ${brand.workbenchName}`
  if (name === 'wb-tools') return `视频去字幕、去水印、视频转2K高清等 - ${brand.workbenchName}`
  if (section) return `${section} - ${brand.workbenchName}`
  return brand.workbenchName
})

const pageDescription = computed(() => {
  const name = String(route.name || '').trim()
  const wb = brand.workbenchName
  if (name === 'wb-tools') return `${wb}支持视频去字幕、去水印、视频转2K高清、视频翻译等多种高效的AI创作辅助工具。是AI视频创作的必备助手，欢迎免费试用。`
  if (name === 'wb-batch') return `${wb}批处理工作台：批量视频去字幕，去水印。一键处理多达200个视频，效率拉满。`
  if (name === 'wb-tasks') return `${wb}任务列表：查看、筛选与管理任务进度，并下载处理结果。`
  if (name === 'wb-recharge') return `${wb}充值与消耗：查看充值记录、消耗记录与账单明细。`
  if (name === 'wb-contact') return `${wb}联系支持：获取客服与问题反馈入口。`
  if (name === 'wb-account') return `${wb}账号管理：个人资料与安全设置。`
  if (name === 'wb-subaccounts') return `${wb}子账号管理：邀请与管理子账号，并进行积分授权与数据查询。`
  return `${wb}快速访问视频与图像处理工具，并管理任务与账号。`
})

const breadcrumbJsonLd = computed(() => {
  const items: any[] = [{ '@type': 'ListItem', position: 1, name: '首页', item: siteUrl }]
  items.push({ '@type': 'ListItem', position: 2, name: brand.workbenchName, item: `${siteUrl}/ai-workbench/` })
  const sec = String(sectionLabel.value || '').trim()
  const tool = String(toolLabel.value || '').trim()
  if (sec) items.push({ '@type': 'ListItem', position: items.length + 1, name: sec, item: canonical.value })
  if (tool) items.push({ '@type': 'ListItem', position: items.length + 1, name: tool, item: pageUrl.value })
  return JSON.stringify({ '@context': 'https://schema.org', '@type': 'BreadcrumbList', '@id': `${canonical.value}#breadcrumb`, itemListElement: items })
})

const webPageJsonLd = computed(() => {
  return JSON.stringify({
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    '@id': `${canonical.value}#webpage`,
    url: canonical.value,
    name: pageTitle.value,
    description: pageDescription.value,
    isPartOf: { '@id': `${siteUrl}#website` },
    breadcrumb: { '@id': `${canonical.value}#breadcrumb` },
  })
})

const jsonLdScripts = computed(() => {
  const out: any[] = []
  const add = (key: string, json: string) => {
    if (!json) return
    out.push({ key, type: 'application/ld+json', textContent: json })
  }
  add('jsonld-webpage', webPageJsonLd.value)
  add('jsonld-breadcrumb', breadcrumbJsonLd.value)
  return out
})

useHead(() => ({
  htmlAttrs: { lang: htmlLang.value },
  title: pageTitle.value,
  meta: [
    { name: 'description', content: pageDescription.value },
    { property: 'og:title', content: pageTitle.value },
    { property: 'og:description', content: pageDescription.value },
    { property: 'og:type', content: 'website' },
    { property: 'og:url', content: canonical.value },
    { property: 'og:image', content: socialImage.value },
    { property: 'og:site_name', content: brand.productName },
    { property: 'og:locale', content: htmlLang.value },
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:title', content: pageTitle.value },
    { name: 'twitter:description', content: pageDescription.value },
    { name: 'twitter:image', content: socialImage.value },
  ],
  link: [{ rel: 'canonical', href: canonical.value }],
  script: jsonLdScripts.value,
}))
</script>

<template>
  <div class="text-on-background h-screen overflow-hidden flex font-body">
    <WorkbenchSidebar />
    <main class="flex-1 flex flex-col min-w-0 bg-white/4 h-full overflow-hidden">
      <WorkbenchTopbar :breadcrumb="breadcrumb" :onRecharge="goRecharge" />
      <div
        class="flex-1 w-full min-h-0"
        :class="[noPageScroll ? 'overflow-hidden' : 'overflow-y-auto', pagePaddingClass]"
      >
        <div class="h-full min-h-0">
          <RouterView />
        </div>
      </div>
    </main>
  </div>
</template>
